import { WebPCodec } from '@playcanvas/splat-transform';
import { Color, createGraphicsDevice } from 'playcanvas';

import { registerAnnotationsEvents } from './annotations';
import { registerBookmarksEvents } from './bookmarks';
import { registerCameraPlaybackEvents } from './camera-playback';
import { CanvasContextMenu } from './canvas-context-menu';
import { registerCameraMotionDocEvents } from './camera-motion-doc';
import { registerSceneCameraEvents } from './scene-camera';
import { registerSceneCameraDocEvents } from './scene-camera-doc';
import { registerSceneCollisionEvents } from './scene-collision';
import { registerCameraPosesEvents } from './camera-poses';
import { registerDocEvents } from './doc';
import { registerElementAnimationEvents } from './element-animation';
import { EditHistory } from './edit-history';
import { registerEditorEvents } from './editor';
import { Events } from './events';
import { initFileHandler } from './file-handler';
import { registerIframeApi } from './iframe-api';
import { registerMeasurementsEvents } from './measurements';
import { registerPicturesEvents } from './pictures';
import { initPicturesRuntime } from './pictures-runtime';
import { registerPlySequenceEvents } from './ply-sequence';
import { registerPublishEvents } from './publish';
import { registerReconstructionTimelineEvents } from './reconstruction-timeline';
import { registerReconstructionTimelinePathEvents } from './reconstruction-timeline-paths';
import { registerRenderEvents } from './render';
import { registerReportGeneratorEvents } from './report-generator';
import { registerImportedMotionEvents } from './imported-motion';
import { Scene } from './scene';
import { getSceneConfig } from './scene-config';
import { registerScenePresets } from './scene-presets';
import { registerScreenshotEvents } from './screenshot';
import { registerSelectionEvents } from './selection';
import { ShortcutManager } from './shortcut-manager';
import { registerTimelineEvents } from './timeline';
import { AnnotationTool } from './tools/annotation-tool';
import { BoxSelection } from './tools/box-selection';
import { BrushSelection } from './tools/brush-selection';
import { EyedropperSelection } from './tools/eyedropper-selection';
import { FloodSelection } from './tools/flood-selection';
import { LassoSelection } from './tools/lasso-selection';
import { MeasureTool } from './tools/measure-tool';
import { MoveTool } from './tools/move-tool';
import { PolygonSelection } from './tools/polygon-selection';
import { RectSelection } from './tools/rect-selection';
import { RotateTool } from './tools/rotate-tool';
import { ScaleTool } from './tools/scale-tool';
import { SphereSelection } from './tools/sphere-selection';
import { ToolManager } from './tools/tool-manager';
import { registerTourPlayerEvents } from './tour-player';
import { registerToursEvents } from './tours';
import { registerTrackManagerEvents } from './track-manager';
import { registerTransformHandlerEvents } from './transform-handler';
import { EditorUI } from './ui/editor';
import { localizeInit } from './ui/localization';
import { registerVehicleMotionDocEvents } from './vehicle-motion-doc';
import { registerVideoExportEvents } from './video-export';

declare global {
    interface LaunchParams {
        readonly files: FileSystemFileHandle[];
    }

    interface Window {
        launchQueue: {
            setConsumer: (callback: (launchParams: LaunchParams) => void) => void;
        };
        scene: Scene;
    }
}

const getURLArgs = () => {
    // extract settings from command line in non-prod builds only
    const config = {};

    const apply = (key: string, value: string) => {
        let obj: any = config;
        key.split('.').forEach((k, i, a) => {
            if (i === a.length - 1) {
                obj[k] = value;
            } else {
                if (!obj.hasOwnProperty(k)) {
                    obj[k] = {};
                }
                obj = obj[k];
            }
        });
    };

    const params = new URLSearchParams(window.location.search.slice(1));
    params.forEach((value: string, key: string) => {
        apply(key, value);
    });

    return config;
};

const main = async () => {
    // root events object
    const events = new Events();

    // Helpers gizmos global toggle.
    // This is stored as an event function so late-added helpers can initialize their local state.
    let helpersGizmosEnabled = false;
    events.function('helpers.gizmos', () => helpersGizmosEnabled);
    events.on('helpers.gizmos', (show: boolean) => {
        helpersGizmosEnabled = !!show;
    });

    // url
    const url = new URL(window.location.href);

    // edit history
    const editHistory = new EditHistory(events);

    // expose edit history queue so subsystems (e.g. transform handlers) can serialize their
    // own async work onto the same chain that gates add/undo/redo.
    events.function('edit.queue', (fn: () => Promise<void>) => editHistory.queue(fn));

    // init localization
    await localizeInit();

    // Configure WebP WASM for SOG format (used for both reading and writing)
    WebPCodec.wasmUrl = new URL('static/lib/webp/webp.wasm', document.baseURI).toString();

    // register events that only need the events object (before UI is created)
    registerTimelineEvents(events);
    registerCameraPosesEvents(events);
    registerElementAnimationEvents(events);
    registerCameraPlaybackEvents(events);
    registerImportedMotionEvents(events);
    registerTrackManagerEvents(events);
    registerTransformHandlerEvents(events);
    registerPlySequenceEvents(events);
    registerPublishEvents(events);
    registerReconstructionTimelineEvents(events);
    registerReconstructionTimelinePathEvents(events);
    registerIframeApi(events);

    // initialize shortcuts
    const shortcutManager = new ShortcutManager(events);
    events.function('shortcutManager', () => shortcutManager);

    // editor ui
    const editorUI = new EditorUI(events);

    // create the graphics device
    const graphicsDevice = await createGraphicsDevice(editorUI.canvas, {
        deviceTypes: ['webgl2'],
        antialias: false,
        depth: false,
        stencil: false,
        xrCompatible: false,
        powerPreference: 'high-performance'
    });

    const overrides = [
        getURLArgs()
    ];

    // resolve scene config
    const sceneConfig = getSceneConfig(overrides);

    // construct the manager
    const scene = new Scene(
        events,
        sceneConfig,
        editorUI.canvas,
        graphicsDevice
    );

    // colors
    const bgClr = new Color();
    const selectedClr = new Color();
    const unselectedClr = new Color();
    const lockedClr = new Color();

    const setClr = (target: Color, value: Color, event: string) => {
        if (!target.equals(value)) {
            target.copy(value);
            events.fire(event, target);
        }
    };

    const setBgClr = (clr: Color) => {
        setClr(bgClr, clr, 'bgClr');
    };
    const setSelectedClr = (clr: Color) => {
        setClr(selectedClr, clr, 'selectedClr');
    };
    const setUnselectedClr = (clr: Color) => {
        setClr(unselectedClr, clr, 'unselectedClr');
    };
    const setLockedClr = (clr: Color) => {
        setClr(lockedClr, clr, 'lockedClr');
    };

    events.on('setBgClr', (clr: Color) => {
        setBgClr(clr);
    });
    events.on('setSelectedClr', (clr: Color) => {
        setSelectedClr(clr);
    });
    events.on('setUnselectedClr', (clr: Color) => {
        setUnselectedClr(clr);
    });
    events.on('setLockedClr', (clr: Color) => {
        setLockedClr(clr);
    });

    events.function('bgClr', () => {
        return bgClr;
    });
    events.function('selectedClr', () => {
        return selectedClr;
    });
    events.function('unselectedClr', () => {
        return unselectedClr;
    });
    events.function('lockedClr', () => {
        return lockedClr;
    });

    events.on('bgClr', (clr: Color) => {
        const cnv = (v: number) => `${Math.max(0, Math.min(255, (v * 255))).toFixed(0)}`;
        document.body.style.backgroundColor = `rgba(${cnv(clr.r)},${cnv(clr.g)},${cnv(clr.b)},1)`;
    });
    events.on('selectedClr', (clr: Color) => {
        scene.forceRender = true;
    });
    events.on('unselectedClr', (clr: Color) => {
        scene.forceRender = true;
    });
    events.on('lockedClr', (clr: Color) => {
        scene.forceRender = true;
    });

    // initialize colors from application config
    const toColor = (value: { r: number, g: number, b: number, a: number }) => {
        return new Color(value.r, value.g, value.b, value.a);
    };
    setBgClr(toColor(sceneConfig.bgClr));
    setSelectedClr(toColor(sceneConfig.selectedClr));
    setUnselectedClr(toColor(sceneConfig.unselectedClr));
    setLockedClr(toColor(sceneConfig.lockedClr));

    // create the mask selection canvas
    const maskCanvas = document.createElement('canvas');
    const maskContext = maskCanvas.getContext('2d');
    maskCanvas.setAttribute('id', 'mask-canvas');
    maskContext.globalCompositeOperation = 'copy';

    const mask = {
        canvas: maskCanvas,
        context: maskContext
    };

    // tool manager
    const toolManager = new ToolManager(events);
    toolManager.register('rectSelection', new RectSelection(events, editorUI.toolsContainer.dom));
    toolManager.register('brushSelection', new BrushSelection(events, editorUI.toolsContainer.dom, mask));
    toolManager.register('floodSelection', new FloodSelection(events, editorUI.toolsContainer.dom, mask, editorUI.canvasContainer));
    toolManager.register('polygonSelection', new PolygonSelection(events, editorUI.toolsContainer.dom, mask));
    toolManager.register('lassoSelection', new LassoSelection(events, editorUI.toolsContainer.dom, mask));
    toolManager.register('sphereSelection', new SphereSelection(events, scene, editorUI.canvasContainer));
    toolManager.register('boxSelection', new BoxSelection(events, scene, editorUI.canvasContainer));
    toolManager.register('eyedropperSelection', new EyedropperSelection(events, editorUI.toolsContainer.dom, editorUI.canvasContainer));
    toolManager.register('move', new MoveTool(events, scene));
    toolManager.register('rotate', new RotateTool(events, scene));
    toolManager.register('scale', new ScaleTool(events, scene));
    toolManager.register('measure', new MeasureTool(events, scene, editorUI.toolsContainer.dom, editorUI.canvasContainer));
    toolManager.register('annotate', new AnnotationTool(events, scene, editorUI.toolsContainer.dom, editorUI.canvasContainer));

    (scene as any).toolManager = toolManager;

    // Right-click context menu on the 3D canvas (always active, independent of tool selection).
    new CanvasContextMenu(events, scene, editorUI.canvasContainer);

    editorUI.toolsContainer.dom.appendChild(maskCanvas);

    window.scene = scene;

    // register events that need scene or other dependencies
    registerSceneCollisionEvents(scene);
    registerEditorEvents(events, editHistory, scene);
    registerAnnotationsEvents(events);
    registerScenePresets(events);
    registerBookmarksEvents(events);
    registerToursEvents(events);
    registerTourPlayerEvents(events);
    registerVideoExportEvents(events);
    registerMeasurementsEvents(events);
    registerPicturesEvents(events);
    registerReportGeneratorEvents(events);
    initPicturesRuntime(scene, events);
    registerSelectionEvents(events, scene);
    registerScreenshotEvents(scene, events);
    registerCameraMotionDocEvents(scene, events);
    registerSceneCameraEvents(scene, events);
    registerSceneCameraDocEvents(scene, events);
    registerVehicleMotionDocEvents(scene, events);
    registerDocEvents(scene, events);
    registerRenderEvents(scene, events);
    initFileHandler(scene, events, editorUI.appContainer.dom);

    // load async models
    scene.start();

    // handle load params
    const loadList = url.searchParams.getAll('load');
    const filenameList = url.searchParams.getAll('filename');
    for (const [i, value] of loadList.entries()) {
        const decoded = decodeURIComponent(value);
        let filename: string;
        if (i < filenameList.length) {
            filename = decodeURIComponent(filenameList[i]);
        } else {
            try {
                filename = new URL(decoded).pathname.split('/').pop();
            } catch {
                filename = decoded.split('/').pop();
            }
        }

        await events.invoke('import', [{
            filename,
            url: decoded
        }]);
    }


    // handle OS-based file association in PWA mode
    if ('launchQueue' in window) {
        window.launchQueue.setConsumer(async (launchParams: LaunchParams) => {
            for (const file of launchParams.files) {
                await events.invoke('import', [{
                    filename: file.name,
                    contents: await file.getFile()
                }]);
            }
        });
    }

};

export { main };
